import {
  BackButton,
  Box,
  ButtonGroup,
  ButtonLight,
  ButtonOutline,
  ButtonPrimary,
  Tab,
  TabList,
  Tabs
} from 'front-end/src/components'
import { NetworkStatusContext } from 'front-end/src/components/private-route/PrivateRoute'
import { TopicVersionType } from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { useWorkspaceManageTopicsAndThemesQuery } from 'front-end/src/pages/manage-workspace-page/resources-subpage/workspaceManageTopicsAndThemes.codegen'
import { get, isEqual } from 'lodash'
import React, { FC, useContext, useEffect, useState } from 'react'
import { useLocation, useRouteMatch } from 'react-router-dom'
import { brandColors } from 'shared/basicPalette'
import { isColorDark } from 'shared/colorUtils'

import { t, Trans } from '@lingui/macro'

import { linkBuilder } from 'shared/linkBuilder'
import { ScormStatusEnum } from 'types/ScormCourse'
import { useModal } from '../../../../app/hooks/useModal'
import { navigateTo } from '../../../../app/util/appHistory'
import { Dropdown } from '../../../../components/common/dropdown/Dropdown'
import { IconArrowDown } from '../../../../components/common/icon/IconArrowDown'
import IconDoc from '../../../../components/common/icon/IconDoc'
import IconEye from '../../../../components/common/icon/IconEye'
import IconPencil from '../../../../components/common/icon/IconPencil'
import IconSettings from '../../../../components/common/icon/IconSettings'
import { notification } from '../../../../components/common/notifications/Notifications'
import Spinner, {
  SpinnerSizeEnum
} from '../../../../components/common/spinner/Spinner'
import Tooltip from '../../../../components/common/tooltip/Tooltip'
import {
  Topbar,
  TopbarCenter,
  TopbarLeft,
  TopbarRight,
  TopbarTitle
} from '../../../../components/common/topbar/Topbar'
import { apolloClientPrivate } from '../../../../graphql/apolloClient'
import { TopicModelAsEditor } from '../../../../graphql/public/PublicSchemaTypes'
import { useLooopTheme } from '../../../../public-api/hooks/useLooopTheme'
import { PublishModal } from '../../../topic-edit-page/components/publish-modal/PublishModal'
import { ResourceUnpublishModal } from '../../../topic-edit-page/components/unpublish-modal/ResourceUnpublishModal'
import {
  TopicModelAsEditorWithFeedbacksCount,
  useTopicForEditQuery
} from '../../../topic-edit-page/TopicEditPage'
import { videoAssessmentTopicVersionTypesForFE } from '../../StudentTopicDetail'
import ResourceDrawer from '../settings/ResourceDrawer'
import { draftIsEqualPublished } from '../utils/draftIsEqualPublished'
import {
  UnpublishTopicDocument,
  UnpublishTopicMutation,
  UnpublishTopicMutationVariables
} from './operations/unpublishTopic.codegen'
import { useIsTopicValidMutation } from './TopbarEdit.codegen'

const TopbarEdit: FC = () => {
  const { theme } = useLooopTheme()
  const { pathname } = useLocation()
  const isPreview = pathname.endsWith('/preview')
  const [bongoStatus, setBongoStatus] = useState(false)
  const { refetch: fetchTopic } = useTopicForEditQuery()
  const matchRoute = useRouteMatch<{ parentPath: string }>({
    path: ':parentPath(.*)?/topic/:topicId/(.*)?',
    exact: true
  })
  const defaultBackButtonPath = matchRoute?.params?.parentPath
  const { open: openPublish, currentModal: publishModal } = useModal()
  const { open: openUnpublish, currentModal: unpublishModal } = useModal()
  const [showSettings, setShowSettings] = useState(false)

  const status = useContext(NetworkStatusContext)
  const { data, loading } = useTopicForEditQuery()
  const [isTopicValid] = useIsTopicValidMutation()

  const { refetch: refetchWorkspaceTopicsAndThemes } =
    useWorkspaceManageTopicsAndThemesQuery({ skip: true })

  useEffect(() => {
    const handleBongoStatus = ({ data }) => {
      if (bongoStatus) return
      setBongoStatus(data?.changedState?.saveStatus === 'SUCCESS')
    }

    window.addEventListener('message', handleBongoStatus)

    return () => {
      window.removeEventListener('message', handleBongoStatus)
    }
  }, [])

  if (loading) {
    return null
  }

  if (!data || !data.topic) {
    return null
  }
  const { topic } = data

  if (topic.__typename === 'TopicModelAsEditor') {
    const { draftVersion } = topic
    if (topic.__typename === 'TopicModelAsEditor' && draftVersion) {
      const unpublishedChanges = !draftIsEqualPublished(
        topic.currentTopicVersionAsEditor,
        topic.draftVersion
      )

      const currentVersion = topic.currentTopicVersionAsEditor

      const canPublish = draftVersion.elements && !!draftVersion.elements.length

      const isPublishedScorm =
        currentVersion &&
        isEqual(
          get(draftVersion, 'scormCourse.file_details', null),
          get(currentVersion, 'scormCourse.file_details', null)
        ) &&
        isEqual(draftVersion.backgroundImage, currentVersion.backgroundImage) &&
        !unpublishedChanges

      const canPublishScorm =
        get(draftVersion, 'scormCourse.status') === ScormStatusEnum.COMPLETE

      const canPublishVideoAssessment =
        bongoStatus &&
        (currentVersion?.title !== draftVersion.title ||
          currentVersion?.summary !== draftVersion.summary ||
          currentVersion?.manual_duration !== draftVersion.manual_duration ||
          !isEqual(
            draftVersion.backgroundImage,
            currentVersion?.backgroundImage
          ))

      const isVideoAssessmentSaved = draftVersion.updated_at !== null

      const unPublishTopic = async () => {
        try {
          await apolloClientPrivate.mutate<
            UnpublishTopicMutation,
            UnpublishTopicMutationVariables
          >({
            mutation: UnpublishTopicDocument,
            variables: {
              id: topic.id
            }
          })
          fetchTopic()
          refetchWorkspaceTopicsAndThemes({
            workspaceId: topic.workspace_id
          })
        } catch (e) {
          notification.error(e.response.data.message)
        }
      }

      const showDropdown = topic.currentTopicVersionAsEditor
      const showPublished = draftIsEqualPublished(currentVersion, draftVersion)
      const showPublishButton = !showPublished
      const isUpdatingTopic = status.numPendingMutations > 0
      const dropdownActive =
        draftVersion.topic_type === TopicVersionType.scorm
          ? canPublishScorm && !isPublishedScorm
          : showPublishButton

      const openPublishModal = () => {
        openPublish(PublishModal).then(() => {
          fetchTopic()
          refetchWorkspaceTopicsAndThemes({
            workspaceId: topic.workspace_id
          })
        })
      }

      const GeneralPublish = () => {
        return (
          <>
            {canPublish && showPublishButton && (
              <ButtonPrimary
                borderRightRadius={showDropdown ? 0 : undefined}
                disabled={isUpdatingTopic}
                onClick={async () => {
                  const { data: isTopicValidData } = await isTopicValid({
                    variables: {
                      id: topic.id
                    }
                  })

                  if (
                    (isTopicValidData?.topic as TopicModelAsEditor).isTopicValid
                  ) {
                    openPublishModal()
                  }
                }}
                dataTest="PublishButton"
              >
                <Trans>Publish</Trans>
              </ButtonPrimary>
            )}

            {showPublished && (
              <ButtonLight
                borderRightRadius={showDropdown ? 0 : 'inherit'}
                disabled={true}
              >
                <Trans>Published</Trans>
              </ButtonLight>
            )}
            {!canPublish && (
              <ButtonLight disabled={true}>
                <Trans>Publish</Trans>
              </ButtonLight>
            )}
          </>
        )
      }

      const ScormPublish = () => {
        return (
          <>
            {canPublishScorm && !isPublishedScorm && (
              <ButtonPrimary
                onClick={openPublishModal}
                borderRightRadius={showDropdown ? 0 : 'inherit'}
                dataTest="PublishButton"
              >
                <Trans>Publish</Trans>
              </ButtonPrimary>
            )}
            {isPublishedScorm && (
              <ButtonLight
                borderRightRadius={showDropdown ? 0 : 'inherit'}
                disabled={true}
              >
                <Trans>Published</Trans>
              </ButtonLight>
            )}
            {!canPublishScorm && (
              <ButtonLight
                disabled={true}
                borderRightRadius={showDropdown ? 0 : 'inherit'}
              >
                <Trans>Publish</Trans>
              </ButtonLight>
            )}
          </>
        )
      }

      const VideoAssignmentPublish = () => {
        return (
          <>
            {canPublishVideoAssessment && (
              <ButtonPrimary
                onClick={openPublishModal}
                borderRightRadius={showDropdown ? 0 : 'inherit'}
                dataTest="PublishButton"
              >
                <Trans>Publish</Trans>
              </ButtonPrimary>
            )}
            {topic.currentTopicVersionAsEditor && !canPublishVideoAssessment && (
              <ButtonLight
                borderRightRadius={showDropdown ? 0 : 'inherit'}
                disabled={true}
              >
                <Trans>Published</Trans>
              </ButtonLight>
            )}
            {!topic.currentTopicVersionAsEditor && !canPublishVideoAssessment && (
              <ButtonLight
                disabled={true}
                borderRightRadius={showDropdown ? 0 : 'inherit'}
              >
                Publish
              </ButtonLight>
            )}
          </>
        )
      }

      const isVideoAssessment =
        draftVersion?.topic_type &&
        videoAssessmentTopicVersionTypesForFE.includes(draftVersion?.topic_type)

      return (
        <>
          {publishModal}
          {unpublishModal}
          <ResourceDrawer
            topic={topic as TopicModelAsEditorWithFeedbacksCount}
            show={showSettings}
            onHide={() => setShowSettings(false)}
          />
          <Topbar>
            <TopbarLeft>
              <Tooltip tabIndex={-1} text={<Trans>Back</Trans>}>
                <BackButton
                  mr="8px"
                  defaultPath={
                    defaultBackButtonPath || `/workspace/${topic.workspace_id}`
                  }
                />
              </Tooltip>

              <TopbarTitle>{draftVersion.title}</TopbarTitle>
            </TopbarLeft>

            <TopbarCenter>
              {!isVideoAssessment && (
                <Tabs size="lg" defaultIndex={isPreview ? 1 : 0}>
                  <TabList borderBottom={0}>
                    <Tab
                      borderRadius={0}
                      onClick={() =>
                        navigateTo(linkBuilder.topicEditUrl(topic.id))
                      }
                    >
                      <IconPencil mr="8px" /> <Trans>Edit</Trans>
                    </Tab>
                    <Tab
                      borderRadius={0}
                      onClick={() => {
                        if (status.numPendingMutations > 0) {
                          notification.error(
                            t`You cannot preview with unsaved changes`
                          )
                        }
                        navigateTo(linkBuilder.topicPreviewUrl(topic.id))
                      }}
                    >
                      <IconEye mr="8px" /> <Trans>Preview</Trans>
                    </Tab>
                  </TabList>
                </Tabs>
              )}

              {isVideoAssessment && (
                <Tabs size="lg">
                  <TabList borderBottom={0}>
                    <Tab
                      borderRadius={0}
                      isDisabled
                      onClick={() =>
                        navigateTo(linkBuilder.topicEditUrl(topic.id))
                      }
                    >
                      <IconPencil mr="8px" /> <Trans>Edit</Trans>
                    </Tab>
                  </TabList>
                </Tabs>
              )}
            </TopbarCenter>

            <TopbarRight position="relative">
              <ButtonGroup
                className="ButtonsGroup"
                isAttached
                position="relative"
              >
                {isUpdatingTopic && (
                  <Box position="absolute" left="-42px" top="10px">
                    <Spinner size={SpinnerSizeEnum.small} />
                  </Box>
                )}
                {draftVersion.topic_type === TopicVersionType.scorm && (
                  <ScormPublish />
                )}
                {isVideoAssessment && <VideoAssignmentPublish />}
                {draftVersion.topic_type !== TopicVersionType.scorm &&
                  !isVideoAssessment && <GeneralPublish />}
                {showDropdown && (
                  <Dropdown
                    ml="1px !important"
                    tabIndex={-1}
                    boxShadow={
                      dropdownActive
                        ? `-1px 0 0 ${brandColors.white}`
                        : undefined
                    }
                    content={
                      <a
                        className="DropdownLink"
                        onClick={() => {
                          openUnpublish(ResourceUnpublishModal).then(() => {
                            unPublishTopic()
                          })
                        }}
                      >
                        <IconDoc />
                        <Trans>Return to draft</Trans>
                      </a>
                    }
                  >
                    <ButtonPrimary
                      iconOnly
                      aria-label="Show more"
                      borderLeftRadius={0}
                      backgroundColor={
                        dropdownActive
                          ? theme.colors.buttonPrimary.default
                          : brandColors.grey[100]
                      }
                      height="100%"
                      color={
                        !dropdownActive
                          ? brandColors.grey.dark
                          : isColorDark(theme.colors.buttonPrimary.default)
                          ? brandColors.white
                          : brandColors.grey.dark
                      }
                    >
                      <IconArrowDown display="flex" boxSize="18px" />
                    </ButtonPrimary>
                  </Dropdown>
                )}
              </ButtonGroup>

              {topic.currentTopicVersionAsEditor && (
                <ButtonOutline
                  ml="8px"
                  onClick={() => {
                    navigateTo(linkBuilder.topicUrl(topic.id))
                  }}
                >
                  <Trans>Exit editing</Trans>
                </ButtonOutline>
              )}

              {!isPreview && (
                <Tooltip tabIndex={-1} text={<Trans>Settings</Trans>}>
                  <ButtonOutline
                    ml="8px"
                    iconOnly
                    disabled={isVideoAssessment && !isVideoAssessmentSaved}
                    isActive={showSettings}
                    onClick={() => setShowSettings(!showSettings)}
                  >
                    <IconSettings />
                  </ButtonOutline>
                </Tooltip>
              )}
            </TopbarRight>
          </Topbar>
        </>
      )
    } else {
      return null
    }
  } else {
    return null
  }
}

export default TopbarEdit
